import * as React from 'react'
import { useMemo } from 'react'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Field,
    InteractionTag,
    InteractionTagPrimary,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components'
import useModules from '../../hooks/module/useModules'
import useRoles, { rolesTasks } from '../../hooks/useRoles'
import { useTranslation } from 'react-i18next'
import useUser from '../../hooks/useUser'
import useViewport, { Viewport } from '../../hooks/useViewport'
import UserPicker from '../../components/TagPicker/UserPicker'
import { ModuleType } from '../../context/GlobalContext'
import AttributesList from '../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeEntityDto, AttributeViewType, EntityAttributeType } from '../../hooks/useAttributes'
import { useFormikContext } from 'formik'
import useAxios from '../../hooks/useAxios'
import { SortDirection } from '../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { BaseOverviewFilterProps, BaseOverviewFilterType, generateFromDate, generateToDate, ViewType } from '../BaseOverview/BaseOverviewFilter'
import { compareDate } from '../../helpers/dateHelper'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import { TaskOverviewDto } from '../../config/taskConfig'
import TaskStatusPicker from '../../components/TagPicker/TaskStatusPicker'
import { CalendarArrowRightRegular, CalendarDayRegular, CalendarWorkWeekRegular, FlagFilled } from '@fluentui/react-icons'
import { DateTime } from 'luxon'
import DateRange from '../../components/DatePicker/DateRange'
import FilePicker from '../../components/TagPicker/FilePicker'
import { MenuItemKey } from '../../components/MainMenu'
import useTaskStatuses from '../../hooks/useTaskStatuses'
import BooleanCombobox from '../../components/Combobox/BooleanCombobox'
import { CheckboxOnChangeData } from '@fluentui/react-checkbox'
import BaseHeaderItem from '../BaseOverview/BaseHeaderItem'
import HeaderActionsButtons from './HeaderActionsButtons'
import TaskListItem from './TaskListItem'
import { UserStatus } from '../../config/userConfig'

export const useItemStyles = makeStyles({
    taskItem: {
        '& button[aria-label="flag-action"]': {
            visibility: 'hidden',
            height: '24px',
        },
        '& button[aria-label="flag-highlight-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },
        '& button[aria-label="flag-today-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },
        '& button[aria-label="flag-week-active"]': {
            boxShadow: '1px 1px #888888',
            height: '24px',
        },

        '&:hover': {
            '& button[aria-label="flag-action"]': {
                visibility: 'visible',
            },
        },
    },
    listTaskItem: {
        display: 'grid',
        gap: tokens.spacingVerticalS,
        gridTemplateColumns:
            'var(--baseOverviewAvatarWidth) var(--baseOverviewIconWidth) var(--baseOverviewDateWidth) auto var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewFileWidth) var(--baseOverviewAvatarWidth)',
        gridTemplateRows: '1fr',
    },
    listTaskItemSmall: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'min(24px,auto) min(24px,auto) 24px',
    },
    listTaskItemSmallRow1: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemSmallRow2: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewTimeWidth) var(--baseOverviewIconWidth) auto',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemSmallRow3: {
        display: 'grid',
        gridTemplateColumns:
            'var(--baseOverviewDateWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) var(--baseOverviewIconWidth) auto var(--baseOverviewAvatarWidth)',
        gap: tokens.spacingHorizontalS,
    },
    listTaskItemHighlight: {
        border: '1px solid var(--colorPalettePeachBorderActive)!important',
    },
    listTaskItemOverflow: {
        overflowWrap: 'anywhere',
    },
    listTaskItemTitle: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewTimeWidth) 1fr 1fr',
        gap: tokens.spacingHorizontalS,
    },
    filterTagHighlight: {
        color: tokens.colorPalettePeachBorderActive,
    },
    filterTagToday: {
        color: tokens.colorPaletteRedForeground3,
    },
    filterTagWeek: {
        color: tokens.colorPaletteBlueBorderActive,
    },
})

export type TaskOverviewProps = BaseOverviewProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType>

export type BaseTaskOverviewProps = Partial<TaskOverviewProps> & {
    baseUrl: string

    breadcrumb?: MenuItemKey
    fileId?: string
}

export type BaseTaskOverviewLineItemProps = BaseTaskOverviewProps &
    BaseOverviewLineItemProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType> & {
        listItemSmall: boolean
    }

export type ExtendedTaskOverviewDto = TaskOverviewDto

export type TaskFilterType = AttributeEntityDto &
    BaseOverviewFilterType & {
        from?: string
        to?: string
        assignees?: string[]
        assigners?: string[]
        files?: string[]
        statuses?: string[]
        highlight?: boolean
        today?: boolean
        week?: boolean
        excludeOwnerIsAssignee?: boolean
    }

export type TaskSorting = {
    id: string
    due: string
    title: string
    status: string
    assignee: string
    assigner: string
    'file.reference': string
}

export type TaskSortingType = BaseOverviewSortingType<TaskSorting>

type BaseTaskOverviewFilterProps = BaseOverviewFilterProps<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType> & {
    fileId?: string
}

function TaskFilter(props: BaseTaskOverviewFilterProps) {
    const { values, setFieldValue } = useFormikContext<TaskFilterType>()
    const modules = useModules()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
            <Field label={translate('task.filter.assignee')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.assignees || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('assignees', selectedOptions)}
                />
            </Field>
            <Field label={translate('task.filter.assigner')} orientation={orientation}>
                <UserPicker
                    selectedOptions={values.assigners || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('assigners', selectedOptions)}
                />
            </Field>
            {!props.fileId && (
                <Field label={translate('task.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={values.files || []}
                        onOptionsSelect={(_, selectedOptions) => updateFieldValue('files', selectedOptions)}
                    />
                </Field>
            )}
            <Field label={translate('task.filter.status.label')} orientation={orientation}>
                <TaskStatusPicker
                    selectedOptions={values.statuses || []}
                    onOptionsSelect={(_, selectedOptions) => updateFieldValue('statuses', selectedOptions)}
                />
            </Field>
            <Field label={translate('task.filter.highlight')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.highlight?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) =>
                        updateFieldValue('highlight', data.checked === 'mixed' ? undefined : data.checked)
                    }
                />
            </Field>
            <Field label={translate('task.filter.today')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.today?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) => updateFieldValue('today', data.checked === 'mixed' ? undefined : data.checked)}
                />
            </Field>
            <Field label={translate('task.filter.week')} orientation={orientation}>
                <BooleanCombobox
                    selectedOptions={[values.week?.toString() || 'mixed']}
                    onOptionSelect={(_, data: CheckboxOnChangeData) => updateFieldValue('week', data.checked === 'mixed' ? undefined : data.checked)}
                />
            </Field>
            {modules.has(ModuleType.Attributes) && (
                <Accordion collapsible>
                    <AccordionItem value="attributes">
                        <AccordionHeader>{translate('task.filter.attributes')}</AccordionHeader>
                        <AccordionPanel>
                            <AttributesList
                                orientation={orientation}
                                entityType={EntityAttributeType.File}
                                context={[AttributeContextType.File]}
                                viewType={AttributeViewType.Filter}
                                entity={values}
                                setFieldValue={(field, value) => updateFieldValue(values[field], value)}
                            />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    )
}

const filterTagItems: TaskFilterType[] = [
    {
        from: generateFromDate('history'),
        to: generateToDate('history'),
        tag: 'history',
        icon: <CalendarDayRegular />,
        title: 'task.filter.history',
    },
    {
        from: generateFromDate('7days'),
        to: generateToDate('7days'),
        tag: '7days',
        icon: <CalendarWorkWeekRegular />,
        title: 'task.filter.7days',
    },
    {
        from: generateFromDate('all'),
        to: generateToDate('all'),
        tag: 'all',
        icon: <CalendarArrowRightRegular />,
        title: 'task.filter.all',
    },
]

const headerSortingItems: TaskSortingType[] = [
    { property: ['due', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc', default: true },
    { property: ['due', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.datedesc' },
    { property: ['title', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.titleaz' },
    { property: ['title', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.titleza' },
    { property: ['file.reference', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.referenceasc' },
    {
        property: ['file.reference', 'id'],
        direction: SortDirection.Descending,
        descriptionKey: 'sorting.referencedesc',
    },
]

function HeaderFilterTags(props: BaseTaskOverviewFilterProps) {
    const { values, setValues } = useFormikContext<TaskFilterType>()
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { taskStatuses, getStatusName } = useTaskStatuses()
    const { viewport, isViewport } = useViewport()

    const filterMedium = useMemo<boolean>(() => isViewport('<', Viewport.Medium), [viewport])

    return (
        <div className={overviewStyles.gridGapXS}>
            <div className={mergeClasses(overviewStyles.flexGap, overviewStyles.flexWrap)}>
                {filterTagItems.map((tag, index) => (
                    <InteractionTag
                        size={filterMedium ? 'small' : 'medium'}
                        key={index}
                        selected={values.tag === tag.tag}
                        onClick={() => {
                            setValues({ ...values, ...tag })
                        }}
                    >
                        <InteractionTagPrimary icon={tag.icon}> {translate(tag.title)}</InteractionTagPrimary>
                    </InteractionTag>
                ))}
            </div>
            <div className={mergeClasses(overviewStyles.flexGap, overviewStyles.flexWrap)}>
                <InteractionTag
                    size={filterMedium ? 'small' : 'medium'}
                    selected={values.tag === 'flag-highlight' && values.highlight !== undefined}
                    onClick={() =>
                        setValues({
                            ...values,
                            from: undefined,
                            to: undefined,
                            highlight: values.highlight ? undefined : true,
                            today: undefined,
                            week: undefined,
                            tag: !values.highlight ? 'flag-highlight' : 'all',
                        })
                    }
                >
                    <InteractionTagPrimary icon={<FlagFilled className={itemStyles.filterTagHighlight} />}>
                        {translate('task.filter.highlight')}
                    </InteractionTagPrimary>
                </InteractionTag>
                <InteractionTag
                    size={filterMedium ? 'small' : 'medium'}
                    selected={values.tag === 'flag-today' && values.today !== undefined}
                    onClick={() =>
                        setValues({
                            ...values,
                            from: undefined,
                            to: undefined,
                            highlight: undefined,
                            today: values.today ? undefined : true,
                            week: undefined,
                            tag: !values.today ? 'flag-today' : 'all',
                        })
                    }
                >
                    <InteractionTagPrimary icon={<FlagFilled className={itemStyles.filterTagToday} />}>
                        {translate('task.filter.today')}
                    </InteractionTagPrimary>
                </InteractionTag>
                <InteractionTag
                    size={filterMedium ? 'small' : 'medium'}
                    selected={values.tag === 'flag-week' && values.week !== undefined}
                    onClick={() =>
                        setValues({
                            ...values,
                            from: undefined,
                            to: undefined,
                            highlight: undefined,
                            today: undefined,
                            week: values.week ? undefined : !values.week,
                            tag: !values.week ? 'flag-week' : 'all',
                        })
                    }
                >
                    <InteractionTagPrimary icon={<FlagFilled className={itemStyles.filterTagWeek} />}>
                        {translate('task.filter.week')}
                    </InteractionTagPrimary>
                </InteractionTag>
            </div>
            {props.viewType === ViewType.ListView && (
                <div className={mergeClasses(overviewStyles.flexGap, overviewStyles.flexWrap)}>
                    {taskStatuses.map((status, index) => (
                        <InteractionTag
                            size={filterMedium ? 'small' : 'medium'}
                            key={index}
                            selected={values.statuses?.includes(status.alpha)}
                            onClick={() =>
                                setValues({
                                    ...values,
                                    statuses: values.statuses.includes(status.alpha)
                                        ? values.statuses.filter(value => value !== status.alpha)
                                        : [...values.statuses, status.alpha],
                                    tag: undefined,
                                })
                            }
                        >
                            <InteractionTagPrimary>{getStatusName(status.alpha)}</InteractionTagPrimary>
                        </InteractionTag>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function BaseTaskOverview(props: BaseTaskOverviewProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const roles = useRoles()
    const { currentUser } = useUser()
    const { viewport, isViewport } = useViewport()
    const { loaded: tasksLoaded, activeTaskStatusCodes, getStartStatus, getStatusName } = useTaskStatuses()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    const defaultFilterValues = useMemo<TaskFilterType>(
        () => ({
            from: '',
            to: '',
            assignees: roles.any(rolesTasks) && currentUser.status === UserStatus.Enabled ? [currentUser.id] : [],
            assigners: [],
            files: [],
            statuses: activeTaskStatusCodes,
            attributes: [],
            query: '',
            tag: '7days',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues, tasksLoaded],
    )

    const fixedColumnFilterValues = (viewType: ViewType, values: TaskFilterType): TaskFilterType[] => {
        return viewType === ViewType.ListView ? [{}] : values.statuses.map(status => ({ statuses: [status], title: getStatusName(status) }))
    }

    const countItems = (column?: number, filterValues?: TaskFilterType): Promise<BaseOverviewCount> => {
        return axios.get('/task/count', { params: parseFilterValuesToParams(filterValues) }).then(response => response.data)
    }

    const loadItems = (
        column?: number,
        filterValues?: TaskFilterType,
        sortingValues?: TaskSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedTaskOverviewDto[]> => {
        return axios
            .get('/task', { params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) } })
            .then((response: AxiosResponse<ExtendedTaskOverviewDto[]>) => response?.data || [])
    }

    return (
        <BaseOverview<ExtendedTaskOverviewDto, TaskFilterType, TaskSortingType>
            defaultFilterValues={defaultFilterValues}
            fixedColumnFilterValues={fixedColumnFilterValues}
            countItems={countItems}
            loadItems={loadItems}
            listColumnFullWidth
            listItemHeaderContent={BaseHeaderItem}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            listItemContent={(props: BaseTaskOverviewLineItemProps) => <TaskListItem {...props} />}
            listItemClassnames={props => [
                itemStyles.taskItem,
                props.viewType === ViewType.ListView ? itemStyles.listTaskItem : itemStyles.listTaskItemSmall,
                props.viewType === ViewType.ListView && listItemSmall && itemStyles.listTaskItemSmall,
                props.item?.highlight && itemStyles.listTaskItemHighlight,
                props.item &&
                    props.item.status === getStartStatus().alpha &&
                    compareDate(props.item.due, DateTime.now().startOf('day')) < 0 &&
                    overviewStyles.colorOrange,
            ]}
            headerDefaultFilterTags={HeaderFilterTags}
            headerSortingItems={headerSortingItems}
            headerActionsButtons={HeaderActionsButtons}
            filterContent={TaskFilter}
            filterFromJson={values => {
                return {
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    to: values.tag ? generateToDate(values.tag) : values.to,
                }
            }}
            filterToJson={values => {
                return {
                    ...values,
                    from: values.tag ? '' : values.from,
                    to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                }
            }}
            inputPlaceholderKey={'task.filter.search'}
            kanbanEmptyPlaceholderKey={'task.view.empty'}
            {...props}
        />
    )
}
